import React, { useState, useEffect } from 'react';
import { contentService, SiteContent } from '../services/content';
import { Save, AlertCircle, Check } from 'lucide-react';

const availableSections = [
  { key: 'terminos-y-condiciones', title: 'Términos y Condiciones' },
  { key: 'politica-de-privacidad', title: 'Políticas de Privacidad' },
];

const ContentManager: React.FC = () => {
  const [sectionKey, setSectionKey] = useState('terminos-y-condiciones');
  const [currentContent, setCurrentContent] = useState<SiteContent>({ key: 'terminos-y-condiciones', title: '', content: '', updated_at: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadContent(sectionKey);
  }, [sectionKey]);

  const loadContent = async (key: string) => {
    setLoading(true);
    setMessage(null);
    try {
      const data = await contentService.get(key);
      setCurrentContent(data);
    } catch (err: any) {
      if (err.response && err.response.status === 404) {
        const section = availableSections.find(s => s.key === key);
        setCurrentContent({ 
          key: key, 
          title: section?.title || '', 
          content: '', 
          updated_at: '' 
        });
      } else {
        console.error('Error loading content:', err);
        setMessage({ type: 'error', text: 'Error al cargar el contenido.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const section = availableSections.find(s => s.key === sectionKey);
    if (!section) {
      setMessage({ type: 'error', text: 'La sección seleccionada no es válida.' });
      setSaving(false);
      return;
    }

    try {
      const updated = await contentService.update(sectionKey, {
        title: section.title,
        content: currentContent.content
      });
      setCurrentContent(updated);
      setMessage({ type: 'success', text: 'Contenido guardado exitosamente.' });
    } catch (err) {
      console.error('Error saving content:', err);
      setMessage({ type: 'error', text: 'Error al guardar el contenido.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Gestión de Contenido</h2>
        
        {message && (
          <div className={`my-4 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <p>{message.text}</p>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-6">
          <div>
            <label htmlFor="section-select" className="block text-sm font-medium text-gray-700 mb-2">
              Apartado
            </label>
            <select
              id="section-select"
              value={sectionKey}
              onChange={(e) => setSectionKey(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 bg-white rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            >
              {availableSections.map(section => (
                <option key={section.key} value={section.key}>
                  {section.title}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contenido
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Puedes usar saltos de línea para separar párrafos. El texto se mostrará tal cual lo escribas.
              </p>
              <textarea
                value={currentContent.content}
                onChange={(e) => setCurrentContent({ ...currentContent, content: e.target.value })}
                rows={20}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent font-mono text-sm"
                placeholder="Escribe aquí el contenido..."
                required
              />
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button
              type="submit"
              disabled={saving || loading}
              className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Guardar Cambios</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentManager;
